import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile 
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { createUser, getUserData } from '../firebase/dbService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, displayName) {
    try {
      // Create the user authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user profile with display name
      await updateProfile(userCredential.user, { displayName });
      
      // Create the user data document
      await createUser(userCredential.user.uid, {
        email,
        displayName,
        settings: {
          notifications: true,
          dailyScreenTimeGoal: 120, // default 2 hours (in minutes)
          dailySocialMediaGoal: 60, // default 1 hour (in minutes)
          mindfulBreaks: true,
          sleepGuardian: true,
          sleepGuardianStartTime: '22:00', // 10:00 PM
          focusSessions: true
        }
      });
      
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  // Wrap fetchUserData in useCallback to prevent dependency cycles
  const memoizedFetchUserData = useCallback(async () => {
    if (currentUser) {
      const result = await getUserData(currentUser.uid);
      if (result.success) {
        setUserData(result.data);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);
      
      if (user) {
        // Fetch user data whenever the auth state changes
        await memoizedFetchUserData();
      } else {
        setUserData(null);
      }
    });

    return unsubscribe;
  }, [memoizedFetchUserData]);

  const value = {
    currentUser,
    userData,
    signup,
    login,
    logout,
    fetchUserData: memoizedFetchUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 